<script>
  import Layout from '../components/Layout.svelte';
  import ErrorBanner from '../components/ErrorBanner.svelte';
  import { anagraficaImportApi } from '../stores/api.js';
  import { activeCompanyId, activeCompany } from '../stores/company.js';
  import { t, tImmediate } from '../i18n/index.js';
  import { Upload, Users, Truck, AlertTriangle, CheckCircle, XCircle, Loader2, FileSpreadsheet } from '../lib/icons/index.js';

  // See PaymentMethods.svelte for rationale.
  export let embedded = false;

  /** Mirrors MAX_ANAGRAFICA_FILE_BYTES on the server, which answers 413 above it.
   *  Checked here too so an oversized file is explained instantly, instead of
   *  after the browser has uploaded several megabytes to be refused. */
  const MAX_FILE_BYTES = 6 * 1024 * 1024;

  let kind = 'cliente'; // 'cliente' | 'fornitore'
  let file = null;
  let dragOver = false;
  let busy = false;
  let result = null;
  let error = '';

  function handleFileInput(e) {
    file = e.target.files?.[0] || null;
    e.target.value = '';
    result = null;
    error = '';
  }
  function handleDrop(e) {
    e.preventDefault();
    dragOver = false;
    file = e.dataTransfer?.files?.[0] || null;
    result = null;
    error = '';
  }
  function handleDragOver(e) { e.preventDefault(); dragOver = true; }
  function handleDragLeave() { dragOver = false; }

  async function fileToBase64(f) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => {
        const dataUrl = String(r.result || '');
        const i = dataUrl.indexOf(',');
        resolve(i >= 0 ? dataUrl.slice(i + 1) : dataUrl);
      };
      r.onerror = () => reject(r.error || new Error('read failed'));
      r.readAsDataURL(f);
    });
  }

  async function startImport() {
    if (!file) {
      error = tImmediate('anagraficaImport.errors.noFile');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      error = tImmediate('anagraficaImport.errors.tooLarge');
      return;
    }
    busy = true;
    error = '';
    result = null;
    try {
      const contentBase64 = await fileToBase64(file);
      result = await anagraficaImportApi.importFile($activeCompanyId, {
        kind, filename: file.name, contentBase64,
      });
    } catch (err) {
      error = err.message;
    } finally {
      busy = false;
    }
  }

  function statusColor(s) {
    return { imported: 'var(--color-success)', skipped: 'var(--color-warning)', failed: 'var(--color-danger)' }[s] || 'var(--color-text-muted)';
  }
  function statusIcon(s) {
    return { imported: CheckCircle, skipped: AlertTriangle, failed: XCircle }[s] || AlertTriangle;
  }
</script>

{#snippet body()}
  <div class="max-w-5xl mx-auto">
    {#if !embedded}
      <div class="flex items-center gap-3 mb-2">
        <h1 class="section-title mb-0">{$t('anagraficaImport.title')}</h1>
      </div>
    {/if}
    <p class="view-subtitle mt-2 mb-6">{$t('anagraficaImport.subtitle')}</p>

    <!-- Discovery banner -->
    <div class="alert alert-accent mb-6">
      <FileSpreadsheet size={18} class="alert-mark mt-0.5" />
      <div class="text-xs space-y-2 measure" style="color: var(--color-text-body);">
        <p><strong>{$t('anagraficaImport.help.whereTitle')}</strong> {$t('anagraficaImport.help.whereBody')}</p>
        <p><strong>{$t('anagraficaImport.help.formatsTitle')}</strong> {$t('anagraficaImport.help.formatsBody')}</p>
        <p><strong>{$t('anagraficaImport.help.dedupeTitle')}</strong> {$t('anagraficaImport.help.dedupeBody')}</p>
        <p><strong>{$t('anagraficaImport.help.limitsTitle')}</strong> {$t('anagraficaImport.help.limitsBody')}</p>
      </div>
    </div>

    {#if !$activeCompany}
      <div class="card p-4 mb-4 text-sm font-mono" style="color: var(--color-text-muted);">
        {$t('anagraficaImport.errors.noCompany')}
      </div>
    {:else}
      <!-- Kind picker -->
      <div class="card p-6 mb-4">
        <h2 class="eyebrow eyebrow-lg mb-3" style="color: var(--color-primary);">{$t('anagraficaImport.kindLabel')}</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <button type="button" on:click={() => { kind = 'cliente'; result = null; }}
            class="flex items-start gap-3 p-4 border-w text-left transition-colors {kind === 'cliente' ? 'border-action bg-primary-500/10' : ''}"
            style={kind !== 'cliente' ? 'border-color: var(--color-tint-medium);' : ''}>
            <Users size={20} class="flex-shrink-0 mt-1" style="color: {kind === 'cliente' ? 'var(--color-primary)' : 'var(--color-text-muted)'};" />
            <div>
              <p class="eyebrow eyebrow-lg" style="color: {kind === 'cliente' ? 'var(--color-primary)' : 'var(--color-text-body)'};">{$t('anagraficaImport.kindOption.cliente.title')}</p>
              <p class="text-xs font-mono mt-1" style="color: var(--color-text-muted);">{$t('anagraficaImport.kindOption.cliente.body')}</p>
              <p class="text-xs font-mono mt-1" style="color: var(--color-text-muted);">{$t('anagraficaImport.kindOption.cliente.expected')}</p>
            </div>
          </button>
          <button type="button" on:click={() => { kind = 'fornitore'; result = null; }}
            class="flex items-start gap-3 p-4 border-w text-left transition-colors {kind === 'fornitore' ? 'border-action bg-primary-500/10' : ''}"
            style={kind !== 'fornitore' ? 'border-color: var(--color-tint-medium);' : ''}>
            <Truck size={20} class="flex-shrink-0 mt-1" style="color: {kind === 'fornitore' ? 'var(--color-primary)' : 'var(--color-text-muted)'};" />
            <div>
              <p class="eyebrow eyebrow-lg" style="color: {kind === 'fornitore' ? 'var(--color-primary)' : 'var(--color-text-body)'};">{$t('anagraficaImport.kindOption.fornitore.title')}</p>
              <p class="text-xs font-mono mt-1" style="color: var(--color-text-muted);">{$t('anagraficaImport.kindOption.fornitore.body')}</p>
              <p class="text-xs font-mono mt-1" style="color: var(--color-text-muted);">{$t('anagraficaImport.kindOption.fornitore.expected')}</p>
            </div>
          </button>
        </div>
      </div>

      <!-- File drop zone -->
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <div
        on:drop={handleDrop}
        on:dragover={handleDragOver}
        on:dragleave={handleDragLeave}
        class="card p-8 mb-4 border-w border-dashed text-center transition-colors {dragOver ? 'bg-primary-500/10' : ''}"
        style="border-color: {dragOver ? 'var(--color-primary)' : 'var(--color-tint-strong)'};"
      >
        <Upload size={36} class="mx-auto mb-3" style="color: var(--color-primary);" />
        <p class="text-sm font-bold mb-1" style="color: var(--color-text-heading);">{$t('anagraficaImport.dropZone.title')}</p>
        <p class="text-xs font-mono mb-4" style="color: var(--color-text-muted);">{$t('anagraficaImport.dropZone.subtitle')}</p>
        <label class="btn btn-primary btn-sm cursor-pointer">
          {$t('anagraficaImport.dropZone.pick')}
          <input type="file" accept=".xls,.xlsx,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv" class="hidden" on:change={handleFileInput} />
        </label>
        {#if file}
          <p class="mt-4 text-xs font-mono" style="color: var(--color-primary);">
            {file.name} · {(file.size / 1024).toFixed(1)} KB
          </p>
        {/if}
      </div>

      <ErrorBanner {error} />

      {#if result}
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div class="card p-3">
            <span class="text-xs uppercase tracking-wider" style="color: var(--color-text-muted);">{$t('anagraficaImport.summary.total')}</span>
            <p class="text-xl font-bold font-mono" style="color: var(--color-text-heading);">{result.summary.total}</p>
          </div>
          <div class="card p-3">
            <span class="text-xs uppercase tracking-wider" style="color: var(--color-text-muted);">{$t('anagraficaImport.summary.imported')}</span>
            <p class="text-xl font-bold font-mono" style="color: var(--color-success);">{result.summary.imported}</p>
          </div>
          <div class="card p-3">
            <span class="text-xs uppercase tracking-wider" style="color: var(--color-text-muted);">{$t('anagraficaImport.summary.skipped')}</span>
            <p class="text-xl font-bold font-mono" style="color: var(--color-warning);">{result.summary.skipped}</p>
          </div>
          <div class="card p-3">
            <span class="text-xs uppercase tracking-wider" style="color: var(--color-text-muted);">{$t('anagraficaImport.summary.failed')}</span>
            <p class="text-xl font-bold font-mono" style="color: var(--color-danger);">{result.summary.failed}</p>
          </div>
        </div>

        <div class="card p-4 mb-4">
          <h2 class="eyebrow eyebrow-lg mb-3" style="color: var(--color-primary);">{$t('anagraficaImport.resultsTitle')}</h2>
          <div class="max-h-96 overflow-y-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b-w sticky top-0" style="border-color: var(--color-tint-medium); background-color: var(--color-surface-dark);">
                  <th class="text-left py-2 px-1 text-xs font-bold uppercase w-12" style="color: var(--color-text-muted);">{$t('anagraficaImport.col.row')}</th>
                  <th class="text-left py-2 px-1 text-xs font-bold uppercase" style="color: var(--color-text-muted);">{$t('anagraficaImport.col.denominazione')}</th>
                  <th class="text-left py-2 px-1 text-xs font-bold uppercase w-32" style="color: var(--color-text-muted);">{$t('anagraficaImport.col.status')}</th>
                  <th class="text-left py-2 px-1 text-xs font-bold uppercase" style="color: var(--color-text-muted);">{$t('anagraficaImport.col.reason')}</th>
                </tr>
              </thead>
              <tbody>
                {#each result.results as r}
                  {@const Icon = statusIcon(r.status)}
                  <tr class="border-b" style="border-color: var(--color-tint-soft);">
                    <td class="py-1 px-1 font-mono text-xs" style="color: var(--color-text-muted);">{r.rowNum}</td>
                    <td class="py-1 px-1 font-mono text-xs truncate max-w-xs" style="color: var(--color-text-body);">{r.denominazione || '-'}</td>
                    <td class="py-1 px-1 font-mono text-xs">
                      <div class="flex items-center gap-1" style="color: {statusColor(r.status)};">
                        <Icon size={12} />
                        {$t(`anagraficaImport.statusLabel.${r.status}`)}
                      </div>
                    </td>
                    <td class="py-1 px-1 font-mono text-xs" style="color: var(--color-text-muted);">{r.reason || ''}</td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        </div>
      {/if}

      <div class="flex items-center justify-end">
        <button on:click={startImport} disabled={busy || !file}
          class="btn btn-primary gap-2">
          {#if busy}
            <Loader2 size={18} class="animate-spin" />
            <span>{$t('anagraficaImport.importing')}</span>
          {:else}
            <Upload size={18} />
            <span>{$t(`anagraficaImport.startImport.${kind}`)}</span>
          {/if}
        </button>
      </div>
    {/if}
  </div>
{/snippet}

{#if embedded}
  {@render body()}
{:else}
  <Layout>
    {@render body()}
  </Layout>
{/if}
